import React, { useState } from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twJoin, twMerge } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import FieldDescribe from './FieldDescribe';
import { ReactComponent as PWEye } from 'assets/icons/pwEye.svg';
import { ReactComponent as PwEyeShow } from 'assets/icons/pwEyeShow.svg';

type ControlledInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string | React.ReactNode;
  labelClassName?: string;
  describe?: React.ReactNode;
  isRequired?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const FormPassInput = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  labelClassName,
  isRequired,
  describe,
  disabled,
  ...props
}: ControlledInputProps<TFormValues>) => {
  const { register, control } = useFormContext<TFormValues>();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-0.5',
        disabled && 'opacity-50',
      )}
    >
      <FieldLabel
        className={labelClassName}
        name={name}
        label={label}
        isRequired={isRequired}
      />
      <div className="relative w-full">
        <input
          {...register(name)}
          {...props}
          disabled={disabled}
          id={name}
          type={isPasswordVisible ? 'text' : 'password'}
          className={twJoin(
            className,
            'border w-full text-btn-black no-spin border-med-gray-3 text-sm h-12 placeholder-med-gray-3 placeholder:font-medium outline-none font-medium focus:border-blue-600 rounded-5 py-1.5 px-3',
            error && 'border-alert-negative bg-error-yellow',
          )}
        />
        <div
          className="absolute inset-y-0 right-0 flex items-center px-2 cursor-pointer"
          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
        >
          {isPasswordVisible ? <PwEyeShow /> : <PWEye />}
        </div>
      </div>
      {error && <FieldError>{error?.message as string}</FieldError>}
      {!error && describe && (
        <FieldDescribe className="gap-1 items-start">{describe}</FieldDescribe>
      )}
    </div>
  );
};

export default FormPassInput;
